import React, { useMemo, useState } from 'react';
import { Box, Button as MuiButton, Chip, Dialog, Divider, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import Text from '../../../components/UI/atoms/Text';
import Button from '../../../components/UI/atoms/Button';
import Input from '../../../components/UI/atoms/Input';
import Card from '../../../components/UI/molecules/Card';
import { authPost } from '../../../services/authCalls';
import { useApp } from '../../../AppContext';

export default function UserTable({ firmId, users, onRefresh }) {
  const { handleMessage } = useApp();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: '', firstName: '', lastName: '', phoneno: '' });
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '', jobTitle: '', industry: '', companyName: '', phone: '' });
  const [pwOpen, setPwOpen] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [pwValue, setPwValue] = useState('');

  const normalizedUsers = useMemo(() => {
    return (users || []).map(u => ({
      userId: u?.userId || u?.UserId,
      email: u?.email || u?.Email,
      firstName: u?.firstName || u?.FirstName,
      lastName: u?.lastName || u?.LastName,
      jobTitle: u?.jobTitle || u?.JobTitle || '',
      industry: u?.industry || u?.Industry || '',
      companyName: u?.companyName || u?.CompanyName || '',
      phone: u?.phone || u?.Phone || u?.phoneno || '',
      isRootUser: u?.isRootUser ?? u?.IsRootUser,
    }));
  }, [users]);

  const close = () => {
    setOpen(false);
    setForm({ email: '', firstName: '', lastName: '', phoneno: '' });
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({ firstName: u.firstName || '', lastName: u.lastName || '', jobTitle: u.jobTitle || '', industry: u.industry || '', companyName: u.companyName || '', phone: u.phone || '' });
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditUser(null);
  };

  const openPw = (u) => { setPwUser(u); setPwValue(''); setPwOpen(true); };
  const closePw = () => { setPwOpen(false); setPwUser(null); };

  const updatePassword = () => {
    authPost('/api/admin/user/update-password', { userId: pwUser.userId, firmId, password: pwValue })
      .then(() => {
        handleMessage('success', 'Password updated');
        closePw();
      })
      .catch(err => {
        const msg = err?.response?.data || err?.response?.headers?.errormessage || 'Unable to update password';
        handleMessage('error', msg);
      });
  };

  const setAsRoot = (userId) => {
    authPost('/api/admin/user/set-root', { userId, firmId })
      .then(() => {
        handleMessage('success', 'Root user updated');
        onRefresh && onRefresh();
      })
      .catch(err => {
        const msg = err?.response?.data || err?.response?.headers?.errormessage || 'Unable to set root user';
        handleMessage('error', msg);
      });
  };

  const updateProfile = () => {
    authPost('/api/admin/user/update-profile', {
      userId: editUser.userId,
      firmId,
      firstName: editForm.firstName,
      lastName: editForm.lastName,
      jobTitle: editForm.jobTitle,
      industry: editForm.industry,
      companyName: editForm.companyName,
      phone: editForm.phone,
    })
      .then(() => {
        handleMessage('success', 'User profile updated');
        closeEdit();
        onRefresh && onRefresh();
      })
      .catch(err => {
        const msg = err?.response?.data || err?.response?.headers?.errormessage || 'Unable to update profile';
        handleMessage('error', msg);
      });
  };

  const addUser = () => {
    authPost('/api/admin/user/add', {
      firmId,
      email: form.email,
      firstName: form.firstName,
      lastName: form.lastName,
      phoneno: form.phoneno,
    })
      .then(() => {
        handleMessage('success', 'User added');
        close();
        onRefresh && onRefresh();
      })
      .catch(err => {
        const msg = err?.response?.data || err?.response?.headers?.errormessage || 'Unable to add user';
        handleMessage('error', msg);
      });
  };

  const removeUser = (userId) => {
    authPost('/api/admin/user/remove', { firmId, userId })
      .then(() => {
        handleMessage('success', 'User removed');
        onRefresh && onRefresh();
      })
      .catch(err => {
        const msg = err?.response?.data || err?.response?.headers?.errormessage || 'Unable to remove user';
        handleMessage('error', msg);
      });
  };

  const sendReset = (userId) => {
    authPost('/api/admin/user/send-reset-password', { firmId, userId })
      .then(() => handleMessage('success', 'Reset password email sent'))
      .catch(err => {
        const msg = err?.response?.data || err?.response?.headers?.errormessage || 'Unable to send reset password email';
        handleMessage('error', msg);
      });
  };

  const sendWelcome = (userId) => {
    authPost('/api/admin/user/send-welcome', { firmId, userId })
      .then(() => handleMessage('success', 'Welcome email sent'))
      .catch(err => {
        const msg = err?.response?.data || err?.response?.headers?.errormessage || 'Unable to send welcome email';
        handleMessage('error', msg);
      });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '16px' }}>
        <Text sx={{ fontWeight: 600, fontSize: '14px' }}>{normalizedUsers.length} user{normalizedUsers.length !== 1 ? 's' : ''}</Text>
        <MuiButton variant='outlined' size='small' onClick={() => setOpen(true)}>+ Add User</MuiButton>
      </Box>

      {(!normalizedUsers || normalizedUsers.length === 0) ? (
        <Box sx={{ py: '16px', textAlign: 'center' }}>
          <Text sx={{ color: 'text.secondary' }}>No users found.</Text>
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size='small'>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' } }}>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell align='right'>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {normalizedUsers.map(u => (
                <TableRow key={u.userId} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                  <TableCell sx={{ fontWeight: 500 }}>{u.firstName} {u.lastName}</TableCell>
                  <TableCell sx={{ fontSize: '13px' }}>{u.email}</TableCell>
                  <TableCell>
                    {u.isRootUser && (
                      <Chip label='Root' size='small' color='primary' sx={{ fontSize: '11px', height: '20px' }} />
                    )}
                  </TableCell>
                  <TableCell align='right'>
                    <Box sx={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                      <MuiButton variant='outlined' size='small' onClick={() => openEdit(u)}>Edit</MuiButton>
                      <MuiButton variant='outlined' size='small' onClick={() => openPw(u)}>Reset PW</MuiButton>
                      <MuiButton variant='outlined' size='small' onClick={() => sendReset(u.userId)}>Send Reset Email</MuiButton>
                      <MuiButton variant='outlined' size='small' onClick={() => sendWelcome(u.userId)}>Welcome</MuiButton>
                      {!u.isRootUser && normalizedUsers.length > 1 && (
                        <MuiButton variant='outlined' size='small' color='warning' onClick={() => setAsRoot(u.userId)}>Set Root</MuiButton>
                      )}
                      {!u.isRootUser && (
                        <MuiButton variant='outlined' size='small' color='error' onClick={() => removeUser(u.userId)}>Remove</MuiButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Dialog onClose={closePw} open={pwOpen}>
        <Card sx={{ margin: 0, width: { xs: '320px', sm: '400px' } }}>
          <Text type='h1' sx={{ mb: '4px' }}>Update Password</Text>
          <Text sx={{ fontSize: '12px', color: 'text.secondary', fontFamily: 'monospace', mb: '12px' }}>{pwUser?.email}</Text>
          <Divider sx={{ mb: '16px' }} />
          <Input label='New Password' id='pw-value' type='password' value={pwValue} onChange={e => setPwValue(e.target.value)} />
          <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', mt: '16px' }}>
            <Button type='outlined' onClick={closePw}>Cancel</Button>
            <Button disabled={!pwValue || pwValue.length < 6} onClick={updatePassword}>Update Password</Button>
          </Box>
        </Card>
      </Dialog>

      <Dialog onClose={closeEdit} open={editOpen}>
        <Card sx={{ margin: 0, width: { xs: '320px', sm: '480px' } }}>
          <Text type='h1' sx={{ mb: '4px' }}>Edit User</Text>
          <Text sx={{ fontSize: '12px', color: 'text.secondary', fontFamily: 'monospace', mb: '12px' }}>{editUser?.email}</Text>
          <Divider sx={{ mb: '16px' }} />
          <Box sx={{ display: 'flex', gap: '12px' }}>
            <Box sx={{ flex: 1 }}><Input label='First Name *' id='ef-firstName' value={editForm.firstName} onChange={e => setEditForm(p => ({ ...p, firstName: e.target.value }))} /></Box>
            <Box sx={{ flex: 1 }}><Input label='Last Name *' id='ef-lastName' value={editForm.lastName} onChange={e => setEditForm(p => ({ ...p, lastName: e.target.value }))} /></Box>
          </Box>
          <Input label='Job Title' id='ef-jobTitle' value={editForm.jobTitle} onChange={e => setEditForm(p => ({ ...p, jobTitle: e.target.value }))} />
          <Box sx={{ display: 'flex', gap: '12px' }}>
            <Box sx={{ flex: 1 }}><Input label='Industry' id='ef-industry' value={editForm.industry} onChange={e => setEditForm(p => ({ ...p, industry: e.target.value }))} /></Box>
            <Box sx={{ flex: 1 }}><Input label='Company' id='ef-companyName' value={editForm.companyName} onChange={e => setEditForm(p => ({ ...p, companyName: e.target.value }))} /></Box>
          </Box>
          <Input label='Phone' id='ef-phone' value={editForm.phone} onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} />
          <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', mt: '16px' }}>
            <Button type='outlined' onClick={closeEdit}>Cancel</Button>
            <Button disabled={!editForm.firstName || !editForm.lastName} onClick={updateProfile}>Save Changes</Button>
          </Box>
        </Card>
      </Dialog>

      <Dialog onClose={close} open={open}>
        <Card sx={{ margin: 0, width: { xs: '320px', sm: '460px' } }}>
          <Text type='h1' sx={{ mb: '12px' }}>Add User</Text>
          <Divider sx={{ mb: '16px' }} />
          <Input label='Email *' id='email' value={form.email} onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))} />
          <Input label='First Name *' id='firstName' value={form.firstName} onChange={(e) => setForm(prev => ({ ...prev, firstName: e.target.value }))} />
          <Input label='Last Name *' id='lastName' value={form.lastName} onChange={(e) => setForm(prev => ({ ...prev, lastName: e.target.value }))} />
          <Input label='Phone' id='phoneno' value={form.phoneno} onChange={(e) => setForm(prev => ({ ...prev, phoneno: e.target.value }))} />
          <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', mt: '16px' }}>
            <Button type='outlined' onClick={() => close()}>Cancel</Button>
            <Button
              disabled={!form.email || !form.firstName || !form.lastName}
              onClick={() => addUser()}
            >
              Add User
            </Button>
          </Box>
        </Card>
      </Dialog>
    </Box>
  );
}
